import is from '@sindresorhus/is';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
    CustomJobProfileTemplateRequest,
    CustomJobProfileTemplateResponse,
} from '../../../../../data-contract/job-profile-template';
import { useApplicationFetchers } from '../../../../../fetchers/use-application-fetchers';
import { companySettingsKeys } from '../../../../api/query-keys';

type SaveTemplateParams = {
    readonly jobArchitectureId: string;
    readonly templateId: string;
    readonly payload: CustomJobProfileTemplateRequest;
};

type MutationContext = {
    readonly previousTemplate: CustomJobProfileTemplateResponse | null | undefined;
};

export const useSaveTemplate = (): UseMutationResult<
    CustomJobProfileTemplateResponse,
    Error,
    SaveTemplateParams,
    MutationContext
> => {
    const { putCustomJobProfileTemplate } = useApplicationFetchers();
    const queryClient = useQueryClient();

    return useMutation<CustomJobProfileTemplateResponse, Error, SaveTemplateParams, MutationContext>({
        mutationFn: async ({ jobArchitectureId, templateId, payload }) => {
            const result = await putCustomJobProfileTemplate({ jobArchitectureId, templateId, payload });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onError: (_error, variables, context) => {
            if (!is.undefined(context?.previousTemplate)) {
                queryClient.setQueryData(
                    companySettingsKeys.customJobProfileTemplate(variables.jobArchitectureId),
                    context.previousTemplate,
                );
            }
        },
        onMutate: async ({ jobArchitectureId }) => {
            const queryKey = companySettingsKeys.customJobProfileTemplate(jobArchitectureId);
            await queryClient.cancelQueries({ queryKey });

            const previousTemplate = queryClient.getQueryData<CustomJobProfileTemplateResponse | null>(queryKey);

            return { previousTemplate };
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: companySettingsKeys.customJobProfileTemplate(variables.jobArchitectureId),
            });
        },
    });
};
